import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { io } from 'socket.io-client';
import LoginForm from './components/LoginForm';
import HomeFeed from './components/HomeFeed';
import FriendsList from './components/FriendsList';
import ChatWindow from './components/ChatWindow';
import AdminPanel from './components/AdminPanel';
import MediaGallery from './components/MediaGallery';

const apiUrl = process.env.REACT_APP_API_URL;
const socketUrl = process.env.REACT_APP_SOCKET_URL;

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [presence, setPresence] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  // The app opens on a decoy news/status screen; chat only appears once revealed by a swipe.
  const [homeOpen, setHomeOpen] = useState(true);

  const socket = useMemo(() => {
    if (!token) return null;
    return io(socketUrl, { auth: { token } });
  }, [token]);

  const handleAuth = useCallback((authUser, authToken) => {
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(authUser));
    setToken(authToken);
    setUser(authUser);
  }, []);

  const handleLogout = useCallback(() => {
    socket?.disconnect();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setSelectedFriend(null);
    setPresence([]);
    setHomeOpen(true);
  }, [socket]);

  useEffect(() => {
    if (!token) return;
    fetch(`${apiUrl}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        // If fetch fails, cached user from localStorage keeps the session alive
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!socket) return;
    socket.on('presence', setPresence);
    // Don't clear presence on every disconnect — a brief wifi blip would
    // otherwise flash "everyone offline" until the next presence broadcast.
    // The server's own presence event (sent on reconnect) is authoritative.
    return () => {
      socket.off('presence');
    };
  }, [socket]);

  const handleSelectFriend = (friend) => {
    setSelectedFriend(friend);
    setSidebarOpen(false);
  };

  if (!token || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d1117] px-4">
        <LoginForm apiUrl={apiUrl} onAuth={handleAuth} />
      </div>
    );
  }

  if (homeOpen) {
    return (
      <div style={{ height: '100%', overflow: 'hidden' }}>
        <HomeFeed onReveal={() => setHomeOpen(false)} />
      </div>
    );
  }

  return (
    // overflow-hidden + fixed height prevents the page from scrolling on mobile
    <div className="flex bg-[#0d1117] text-slate-100" style={{ height: '100%', overflow: 'hidden' }}>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {adminOpen && (
        <AdminPanel
          apiUrl={apiUrl}
          token={token}
          onClose={() => setAdminOpen(false)}
        />
      )}

      {mediaOpen && (
        <MediaGallery
          apiUrl={apiUrl}
          token={token}
          onClose={() => setMediaOpen(false)}
        />
      )}

      {/* Sidebar — slides in on mobile, always visible on desktop */}
      <aside
        className={`
          fixed top-0 left-0 z-40 w-72 transition-transform duration-300 ease-in-out
          lg:static lg:translate-x-0 lg:flex-shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ height: '100%' }}
      >
        <FriendsList
          token={token}
          apiUrl={apiUrl}
          selectedFriend={selectedFriend}
          onSelect={handleSelectFriend}
          presence={presence}
          user={user}
          onLogout={handleLogout}
          onAdmin={user?.is_admin ? () => setAdminOpen(true) : null}
          onMedia={user?.is_admin ? () => setMediaOpen(true) : null}
          onGoHome={() => setHomeOpen(true)}
        />
      </aside>

      {/* Main chat area */}
      <main className="flex flex-1 flex-col overflow-hidden min-w-0">
        <ChatWindow
          token={token}
          apiUrl={apiUrl}
          socket={socket}
          friend={selectedFriend}
          user={user}
          onOpenSidebar={() => setSidebarOpen(true)}
          presence={presence}
        />
      </main>
    </div>
  );
}

export default App;
